import { getPaginatedQueryFields } from '../graphql/schema/getQueryFields';
import columnTitle from '../graphql/schema/columnTitle';
import { EntityType } from '../@types/entityTypes';
import { DocumentNode } from 'graphql';
import Maybe from 'graphql/tsutils/Maybe';

//List of words that typically (always) are irrelevant for viewing in UI.
const forbiddenFields = [
  '.id',
  'concurrencyVersion',
  'contacts', //Contacts is an array on customers/suppliers which isn't valid/won't work as a group by.
];

//List of words that typically (always) are irrelevant for summing for widgets.
const forbiddenSumFields = [
  '.id',
  'concurrencyVersion',
  'systemPrinterId',
  'sortOrder',
  'documentNumber',
  'periodNumber',
  'precision',
  'index',
  'suffix',
  'pricingAttributeIndex',
];

export function getGroupFields(entity: EntityType, altQuery?: DocumentNode) {
  const query = altQuery || entity.default || entity.instance || entity.workspace;
  const availableFields = getPaginatedQueryFields(query, entity.type, true);
  return Object.values(availableFields)
    .filter((column) => {
      if (column.fieldName === 'id') return false;
      if (column.type === 'Object') return false; //Grouping on object fields is not possible - They would all be seen as unique.
      for (let i = 0; i < forbiddenFields.length; i++) {
        if (column.fieldName.includes(forbiddenFields[i])) return false;
      }
      return true; //Consider inverse of numeric fields version - Currently this is used for 'group by' fields, where numbers aren't suitable?
    })
    .sort((left, right) => {
      return (left.title as string).localeCompare(right.title as string);
    })
    .map((field) => {
      //Check for altered column titles per columnTitle.js
      if (columnTitle[entity.type] && columnTitle[entity.type][field.fieldName]) {
        field.name = columnTitle[entity.type][field.fieldName];
      }
      return field;
    });
}

export function getNumericFields(entity: EntityType, altQuery?: DocumentNode) {
  const query: Maybe<DocumentNode> = altQuery || entity.default || entity.instance || entity.workspace;
  const availableFields = getPaginatedQueryFields(query, entity.type, true, undefined, undefined, {
    includeValidation: true,
  });

  return Object.values(availableFields)
    .filter((column) => {
      if (column.fieldName === 'id') return false;
      for (let i = 0; i < forbiddenSumFields.length; i++) {
        if (column.fieldName.includes(forbiddenSumFields[i])) return false;
      }
      if (column.validationRules?.isTransient) return false; //Transient fields should not be 'total'-able, which is what we're using the numeric tag for here.
      return column.type === 'Long' || column.type === 'Int' || column.type === 'BigDecimal';
    })
    .sort((left, right) => {
      return left.fieldName.localeCompare(right.fieldName);
    })
    .map((field) => {
      //Check for altered column titles per columnTitle.js
      if (columnTitle[entity.type] && columnTitle[entity.type][field.fieldName]) {
        field.name = columnTitle[entity.type][field.fieldName];
      }
      return field;
    });
}
